import { AppProvider } from '@shopify/polaris'
import { useAppBridge } from '@shopify/app-bridge-react'
import { QueryClientProvider } from '@tanstack/react-query'
import enTranslations from '@shopify/polaris/locales/en.json'
import '@shopify/polaris/build/esm/styles.css'
import './theme.css'
import { ShopifyErrorBoundary } from './components/ShopifyErrorBoundary'
import { useCallback, useInsertionEffect, useRef } from 'react'
import { ShopifyAppContent } from './ShopifyAppContent'
import { shopifyQueryClient } from './shopifyQueryClient'
import { getAppBridgeShop } from './authenticatedShopBootstrap'
import { shopifyAppLoadingCopy } from '@testfeed/product-language'
import { ShopifyPageSkeleton } from './components/ShopifyPageSkeleton'
import { ShopifyPageContent } from './components/ShopifyPageContent'

function UnresolvedShopifyScope() {
  return (
    <s-page heading={shopifyAppLoadingCopy.loading}>
      <ShopifyPageContent>
        <ShopifyPageSkeleton accessibilityLabel={shopifyAppLoadingCopy.loading} />
      </ShopifyPageContent>
    </s-page>
  )
}

/**
 * Renders the Shopify app shell and initializes shared providers.
 */
export default function App() {
  const shopify = useAppBridge()
  const shopDomain = getAppBridgeShop(shopify)
  const activeShopScopeKey = shopDomain || 'no-shop'
  const activeShopScopeRef = useRef(activeShopScopeKey)
  // Publish the committed shop scope, not a rendered one: a render React discards must not change which
  // scope async work treats as active. Insertion effects run before every layout and passive effect of the
  // commit (including ShopifyAppContent's), so work started in those effects already sees the new scope.
  useInsertionEffect(() => {
    activeShopScopeRef.current = activeShopScopeKey
  }, [activeShopScopeKey])
  const isActiveShopScope = useCallback((scopeKey: string) => activeShopScopeRef.current === scopeKey, [])
  const shopSpecificContent = shopDomain ? (
    <ShopifyAppContent
      key={shopDomain}
      shopify={shopify}
      expectedShop={shopDomain}
      activeShopScopeKey={activeShopScopeKey}
      isActiveShopScope={isActiveShopScope}
    />
  ) : (
    <UnresolvedShopifyScope />
  )

  return (
    <AppProvider i18n={enTranslations}>
      <ShopifyErrorBoundary shopify={shopify}>
        <QueryClientProvider client={shopifyQueryClient}>{shopSpecificContent}</QueryClientProvider>
      </ShopifyErrorBoundary>
    </AppProvider>
  )
}
